import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { getDashboardRoute } from '../utils/getDashboardRoute';
import loginBg from '../assets/login-bg.png'; // Make sure this matches the copied filename

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login, isAuthenticated, user } = useAuth();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const from = location.state?.from?.pathname;
      if (from) {
        navigate(from, { replace: true });
      } else {
        const dashboardRoute = getDashboardRoute(user.roles);
        navigate(dashboardRoute, { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate, location]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await login(formData);
      if (result.success) {
        showSuccess('Login successful!', 2000);
        const from = location.state?.from?.pathname;
        if (from) {
          navigate(from, { replace: true });
        } else if (result.user) {
          const dashboardRoute = getDashboardRoute(result.user.roles);
          navigate(dashboardRoute, { replace: true });
        } else {
          navigate('/profile', { replace: true });
        }
      } else {
        const errorMessage = result.error || 'Login failed';
        setError(errorMessage);
        showError(errorMessage);
        setIsSubmitting(false);
      }
    } catch (err) {
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);
      showError(errorMessage);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-screen flex bg-white overflow-hidden text-gray-900">
      {/* Left Panel - Image Section (Hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gray-900">
        <img
          src={loginBg}
          alt="Office Workspace"
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex flex-col justify-between p-12">
          {/* Logo overlay on image */}
          <div>
            <div className="flex items-center space-x-2 text-white text-2xl font-bold">
              {/* Using Sutherland-ish logo placeholder or simple text if logo doesn't fit white-on-dark */}
              {/* <img src={logo} alt="Logo" className="h-8 brightness-0 invert" /> */}
              <span>AI Genius</span>
            </div>
          </div>

          {/* Testimonial */}
          <div className="text-white max-w-lg">
            <h2 className="text-4xl font-bold leading-tight mb-6">
              "Simply all the tools that my team and I need."
            </h2>
            <div className="flex flex-col">
              <span className="font-semibold text-lg">Karen Yue</span>
              <span className="text-gray-300 text-sm">Director of Digital Marketing Technology</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form Section */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 md:p-12 lg:p-16">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center lg:text-left">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Welcome back to AI Genius</h1>
            <p className="mt-2 text-gray-500">
              Build your design system effortlessly with our powerful component library.
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-150 ease-in-out sm:text-sm"
                  placeholder="alex.jordan@gmail.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition duration-150 ease-in-out sm:text-sm"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <div className="flex items-center justify-center">
              <div className="text-sm">
                <button type="button" className="font-medium text-indigo-600 hover:text-indigo-500 bg-transparent border-none p-0 cursor-pointer">
                  Forgot password?
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#5D5FEF] hover:bg-[#4A4CD0] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? 'Signing in...' : 'Log in'}
              </button>
            </div>

            <p className="text-center text-sm text-gray-600 mt-4">
              Don't have an account?{' '}
              <Link to="/register" className="font-medium text-indigo-600 hover:text-indigo-500">
                Sign up
              </Link>
            </p>

          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
